import { parseISO, format } from 'date-fns';
import capitalize from 'lodash/capitalize';
import compact from 'lodash/compact';

import empty from '#client/utils/empty';

const LOCALIZED_TEXT = {
  registeredAgents: 'businessProfile.registeredAgents',
  formationDate: 'businessProfile.formationDate',
  registrationState: 'businessProfile.registrationState',
  ein: 'businessProfile.ein',
  principalAddress: 'businessProfile.principalAddress',
  mailingAddress: 'businessProfile.mailingAddress',
  none: 'businessProfile.none'
};

const formatValueOrNone = (value, fm) => (value || fm({ id: LOCALIZED_TEXT.none }));

const formatLocationLine = ({ city, state, zip }) => compact([
  city ? `${city},` : '',
  state,
  zip
]).join(' ');

const formatAddressLine = ({ address1, address2 }) => compact([
  address1,
  address2
]).join(' ');

const formatPersonLine = ({ firstName, lastName }) => compact([
  firstName,
  lastName
]).map(capitalize).join(' ');

/**
 * Formats a contact object into an array of display lines for business profile.
 *
 * @param {object} params
 * @param {object | null} params.contact - The contact object containing company and address info.
 * @param {string | null} [params.companyName] - The company name.
 * @param {string | null} [params.firstName] - The first name.
 * @param {string | null} [params.lastName] - The last name.
 * @param {string | null} [params.address1] - The first line of the address.
 * @param {string | null} [params.address2] - The second line of the address.
 * @param {string | null} [params.city] - The city.
 * @param {string | null} [params.state] - The state.
 * @param {string | null} [params.zip] - The zip code.
 * @param {Function} fm - Localization function that takes an object with an `id` property.
 * @returns {string[]|string} An array of formatted lines, or a localized "none" string if no contact.
 *
 * @example
 * const contact = {
 *   companyName: 'Test Company Inc',
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   address1: '123 Main St',
 *   address2: 'Suite 100',
 *   city: 'Test City',
 *   state: 'CA',
 *   zip: '90210'
 * };
 *
 * const result = formatContact({ contact }, fm);
 *
 * // Returns: ['Test Company Inc', '123 Main St Suite 100', 'Test City, CA 90210']
 */

export const formatContact = ({ contact }, fm) => {
  if (!contact) return fm({ id: LOCALIZED_TEXT.none });

  const {
    companyName,
    firstName,
    lastName,
    address1,
    address2,
    city,
    state,
    zip
  } = contact;

  const formattedCompanyLine = companyName || formatPersonLine({ firstName, lastName });

  return compact([
    formattedCompanyLine,
    formatAddressLine({ address1, address2 }),
    formatLocationLine({ city, state, zip })
  ]);
};

export const formatAddress = (address, fm) => {
  if (!address || !address.address1) {
    return fm({ id: LOCALIZED_TEXT.none });
  }

  const {
    address1,
    address2,
    city,
    state,
    zip
  } = address;

  return compact([
    formatAddressLine({ address1, address2 }),
    formatLocationLine({ city, state, zip })
  ]);
};

export const formatEIN = (ein, fm) => {
  if (!ein) return fm({ id: LOCALIZED_TEXT.none });

  if (/^\d{9}$/.test(ein)) {
    return ein.replace(/^(\d{2})(\d{7})$/, '$1-$2');
  }

  return ein;
};

export const getInfos = ({
  principal,
  mailing,
  formationDate,
  domesticJurisdiction,
  registeredAgents,
  ein,
  fm
}) => [
  {
    title: fm({ id: LOCALIZED_TEXT.formationDate }),
    items: [{
      content: formationDate
        ? format(parseISO(formationDate), 'MM/dd/yyyy')
        : fm({ id: LOCALIZED_TEXT.none })
    }]
  },
  {
    title: fm({ id: LOCALIZED_TEXT.registrationState }),
    items: [{ content: formatValueOrNone(domesticJurisdiction, fm) }]
  },
  {
    title: fm({ id: LOCALIZED_TEXT.registeredAgents }),
    items: registeredAgents?.length
      ? registeredAgents.map(({ contact }) => ({
        content: formatContact(empty(contact) ? null : { contact }, fm)
      }))
      : [{ content: fm({ id: LOCALIZED_TEXT.none }) }]
  },
  {
    title: fm({ id: LOCALIZED_TEXT.principalAddress }),
    items: [{ content: formatAddress(principal, fm) }]
  },
  {
    title: fm({ id: LOCALIZED_TEXT.mailingAddress }),
    items: [{ content: formatAddress(mailing, fm) }]
  },
  {
    title: fm({ id: LOCALIZED_TEXT.ein }),
    items: [{ content: formatEIN(ein, fm) }]
  }
];
